import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  InputTextVertical,
  SelectControllerVertical,
  VerticalInputFormLayout,
} from "@components";

import { useMetaData } from "@hooks/inventory/roomConfiguration/useMetaData";
import { useLanguage } from "@hooks/useLanguage";

import {
  getConvertedMetaData,
  getConvertedOfficeData,
} from "@helpers/inventory/metaDataConvert";

import { ROOM_META_DATA } from "@constants/inventory/room-meta-data";
import { RoomCreateType, RoomDetailsResponseType } from "@type/inventory/room";
import { getFloors } from "@helpers/inventory/getFloors";

import {
  ROOM_CONFIG_ROOM,
  roomConfigRoomValidationSchema,
} from "@validations/Inventory/roomConfig/roomConfigRoomValidation";
import { useOfficeList } from "@hooks/inventory/roomConfiguration/useOfficeList";
import { OfficeResponseType } from "@type/inventory/office";
import { t } from "i18next";

const { OFFICE, BUILDING, ROOM_TYPE } = ROOM_META_DATA;

interface RoomFormProps {
  setIsSuccessModalOpen: (arg: boolean) => void;
  setIsErrorModalOpen: (arg: boolean) => void;
  setErrorMessage: (err: string) => void;
  roomState: any;
  updateRoomHandler: (data: RoomCreateType) => void;
  createRoomHandler: (data: RoomCreateType) => void;
}

export default function RoomForm({
  setIsSuccessModalOpen,
  setIsErrorModalOpen,
  setErrorMessage,
  roomState,
  updateRoomHandler,
  createRoomHandler,
}: RoomFormProps) {
  const { language } = useLanguage();

  const [floors, setFloors] = useState([]);

  const [roomId, setRoomId] = useState<number | string>("");

  const {
    getSingleData,
    createError,
    createSuccess,
    updateError,
    updateSuccess,
  } = roomState;
  const { data: roomDetails } =
    getSingleData || ({} as RoomDetailsResponseType);

  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<RoomCreateType>({
    resolver: yupResolver(roomConfigRoomValidationSchema),
  });

  const { roomConfigOfficeState } = useOfficeList(true);

  const { getData } = roomConfigOfficeState;

  const { data: offices } = getData || ({} as OfficeResponseType);

  const { data: buildings, handleQuery: handleBuildingQuery } =
    useMetaData(BUILDING);
  const { data: roomTypes } = useMetaData(ROOM_TYPE);

  const resetAction = () => {
    reset();
    setRoomId("");
  };

  const onSubmit: SubmitHandler<RoomCreateType> = (data) => {
    if (roomId) {
      updateRoomHandler({
        ...data,
        floorId: Number(data.floorId),
        roomCount: Number(data.roomCount),
      });
    } else {
      createRoomHandler({
        ...data,
        floorId: Number(data.floorId),
        roomCount: Number(data.roomCount),
      });
    }
  };

  useEffect(() => {
    if (createSuccess || updateSuccess) {
      reset();
      if (updateSuccess) {
        setRoomId("");
      }
      setIsSuccessModalOpen(createSuccess || updateSuccess);
    }
    setIsErrorModalOpen(createError || updateError);
    if (createError?.response && createError?.response?.data) {
      setErrorMessage(
        createError.response.data.message || "something was wrong",
      );
    }

    if (updateError?.response && updateError?.response?.data) {
      setErrorMessage(
        updateError.response.data.message || "something was wrong",
      );
    }
  }, [
    createSuccess,
    createError,
    updateSuccess,
    updateError,
    reset,
    setIsErrorModalOpen,
    setIsSuccessModalOpen,
    setErrorMessage,
  ]);

  const watchOfficeLocation = watch("officeAddress");
  const watchBuilding = watch("building");

  useEffect(() => {
    if (watchOfficeLocation) {
      handleBuildingQuery("officeId", watchOfficeLocation);
    }
  }, [watch, watchOfficeLocation]);

  useEffect(() => {
    if (watchBuilding && buildings) {
      const updatedFloors = getFloors(watchBuilding, buildings);
      setFloors(updatedFloors as any);
    }
  }, [watch, watchBuilding, buildings]);

  useEffect(() => {
    if (roomDetails) {
      setValue("officeAddress", roomDetails?.building?.officeAddress?.id);
      setValue("building", roomDetails?.building?.id);
      setValue("floorId", roomDetails?.floorId);
      setValue("roomType", roomDetails?.roomType?.id);
      setValue("roomNo", roomDetails?.roomNo);
      setRoomId(roomDetails.id);
    }
  }, [roomDetails, setValue]);

  useEffect(() => resetAction(), []);

  return (
    <VerticalInputFormLayout
      label="INVENTORY_ROOM_CONFIG.ROOM.FORM.HEADER"
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      isUpdate={roomId ? true : false}
      cancelAction={resetAction}
    >
      <SelectControllerVertical
        label="INVENTORY_ROOM_CONFIG.ROOM.FORM.FIELDS.OFFICE"
        name="officeAddress"
        register={{
          ...register(ROOM_CONFIG_ROOM.OFFICE),
        }}
        options={getConvertedOfficeData(offices, language)}
        setValue={setValue}
        control={control}
        error={errors}
        required
        cols={3}
      />
      <SelectControllerVertical
        label="INVENTORY_ROOM_CONFIG.ROOM.FORM.FIELDS.BUILDING"
        name="building"
        register={{
          ...register(ROOM_CONFIG_ROOM.BUILDING),
        }}
        options={getConvertedMetaData(buildings, language)}
        setValue={setValue}
        control={control}
        error={errors}
        required
        cols={3}
      />
      <SelectControllerVertical
        label="INVENTORY_ROOM_CONFIG.ROOM.FORM.FIELDS.FLOOR"
        name="floorId"
        register={{
          ...register(ROOM_CONFIG_ROOM.FLOOR),
        }}
        options={floors}
        setValue={setValue}
        control={control}
        error={errors}
        required
        cols={3}
      />
      <SelectControllerVertical
        label="INVENTORY_ROOM_CONFIG.ROOM.FORM.FIELDS.ROOM_TYPE"
        name="roomType"
        register={{
          ...register(ROOM_CONFIG_ROOM.ROOM_TYPE),
        }}
        options={getConvertedMetaData(roomTypes, language)}
        setValue={setValue}
        control={control}
        error={errors}
        required
        cols={3}
      />
      <InputTextVertical
        label="INVENTORY_ROOM_CONFIG.ROOM.FORM.FIELDS.ROOM_NO"
        register={{
          ...register(ROOM_CONFIG_ROOM.ROOM_NO),
        }}
        error={errors}
        required
        cols={3}
      />
    </VerticalInputFormLayout>
  );
}
